import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { Card } from "clutch/src/Card/Card";

import {
  AccordionContainer,
  Container,
  RankInfo,
  StatsBlock,
} from "@/shared/ProfileRank.style.jsx";
import { classNames } from "@/util/class-names.mjs";

type RankGutsProps = {
  image?: string;
  imageWidth?: number;
  imageHeight?: number;
  imageComponent?: React.ReactNode;
  percentFill?: number;
  textTitle?: string;
  textTitleColor?: string;
  textTopRight?: string;
  barColor?: string;
  isAccordion?: boolean;
  textBottomLeft?: string;
  textBottomRight?: string;
};
type ProfileRankProps = RankGutsProps & {
  title: string;
  percentFill?: number;
  color: string;
  name: string;
  points?: string;
  wins?: number;
  losses?: number;
  belowTitle?: string;
  below?: React.ReactNode;
};
type StatsProps = {
  stats: Array<{
    title: string;
    value: string;
  }>;
};
type AccordionProps = {
  ranksList: Array<{
    [key: string]: unknown;
  }>;
};

function RankGuts({
  image,
  imageWidth,
  imageHeight,
  imageComponent,
  percentFill,
  textTitle,
  textTitleColor,
  textTopRight,
  textBottomLeft,
  textBottomRight,
  barColor = "var(--shade2)",
  isAccordion,
}: RankGutsProps) {
  return (
    <div
      {...classNames(RankInfo(), image && "has-image")}
      style={{
        paddingBlockStart: isAccordion ? "var(--sp-1)" : null,
      }}
    >
      {image ? (
        <img
          src={image}
          className="rank-image"
          width={imageWidth}
          height={imageHeight}
        />
      ) : imageComponent ? (
        <div className="rank-image--component">{imageComponent}</div>
      ) : null}
      <div className="meta">
        <div className="top-line">
          <span
            className="name type-subtitle--bold"
            style={{ color: textTitleColor }}
          >
            {textTitle}
          </span>
          {textTopRight && (
            <span className="points type-caption--bold shade2">
              {textTopRight}
            </span>
          )}
        </div>
        <div
          className="bar"
          style={{
            "--bar-fill": `calc(${percentFill} * 100%)`,
            "--bar-color": barColor,
          }}
        />
        {textBottomLeft || textBottomRight ? (
          <div className="bottom-line type-caption shade2">
            <span>{textBottomLeft}</span>
            <span>{textBottomRight}</span>
          </div>
        ) : null}
      </div>
    </div>
  );
}

function ProfileRank({
  title,
  image,
  imageWidth = 80,
  imageHeight = 80,
  imageComponent,
  percentFill = 0,
  color,
  name,
  points,
  wins = 0,
  losses = 0,
  belowTitle,
  below,
  textBottomLeft = null,
  textBottomRight = null,
}: ProfileRankProps) {
  const { t } = useTranslation();

  const games = wins + losses;
  const winrate = wins / (games || 1);

  return (
    <Card padding="0" title={title} className={Container()}>
      <RankGuts
        image={image}
        imageWidth={imageWidth}
        imageHeight={imageHeight}
        imageComponent={imageComponent}
        percentFill={percentFill}
        textTitle={name}
        textTitleColor={color}
        textTopRight={points}
        textBottomLeft={
          games
            ? t("common:winRatePercent", "{{winRate, percent}} WR", {
                winRate: winrate,
                formatParams: {
                  style: "percent",
                  minimumFractionDigits: 1,
                  maximumFractionDigits: 1,
                },
              })
            : textBottomLeft
        }
        textBottomRight={
          games
            ? t("common:winsDashLosses", "{{wins}}W - {{losses}}L", {
                wins,
                losses,
              })
            : textBottomRight
        }
        barColor={color}
      />
      {/* This is kind of arbitrary content, it's different for each game */}
      {below && (
        <div className="below">
          {belowTitle && (
            <span className="below-title type-callout--bold">{belowTitle}</span>
          )}
          {below}
        </div>
      )}
    </Card>
  );
}

function Stats({ stats = [] }: StatsProps) {
  if (!stats.length) return null;

  return (
    <ul className={StatsBlock()}>
      {stats?.map((stat) => {
        const { title, value } = stat;
        return (
          <li key={title} className="stat-line">
            <p className="type-caption shade1">{title}</p>
            <p className="type-caption--bold">{value}</p>
          </li>
        );
      })}
    </ul>
  );
}

function Accordion({ ranksList = [] }: AccordionProps) {
  const [openItem, setOpenItem] = useState(-1);

  return (
    <Card padding="0" className={AccordionContainer()}>
      {ranksList.map((rank, index) => {
        return (
          <AccordionItem
            key={index}
            {...rank}
            index={index}
            open={openItem === index}
            openItem={openItem}
            setOpenItem={setOpenItem}
          />
        );
      })}
    </Card>
  );
}

function AccordionItem(props) {
  const {
    index,
    label,
    queueIcon,
    rankName,
    percentFill,
    wins,
    losses,
    summaryColor,
    textTitleColor,
    barColor,
    titleOverride,
    open,
    openItem,
    setOpenItem,
    onExpand = () => {},
  } = props;
  const { t } = useTranslation();

  const handleOpen = (e) => {
    // Can eventually replace this when browsers fully supprt named details groups:
    // https://groups.google.com/a/chromium.org/g/blink-dev/c/Bk6xulOmBn8/m/P39eHu8-AQAJ
    e.preventDefault();
    if (index === openItem) {
      setOpenItem(-1);
    } else {
      setOpenItem(index);
      if (typeof onExpand === "function") onExpand();
    }
  };

  const games = wins + losses;
  const winrate = wins / (games || 1);
  const title = titleOverride
    ? titleOverride
    : games
      ? t("common:percentWinrate", "{{winRate, percent}} Win Rate", {
          winRate: winrate,
          formatParams: {
            style: "percent",
            minimumFractionDigits: 1,
            maximumFractionDigits: 1,
          },
        })
      : t("common:noGamesPlayed", "No Games Played");

  return (
    <details open={open} style={{ "--summary-color": summaryColor }}>
      <summary onClick={handleOpen}>
        <div className="summary-label">
          {queueIcon && <div>{queueIcon}</div>}
          <span className="type-callout--semi">{label}</span>
        </div>
        {rankName ? (
          <div className="summary-rank">
            <span className="type-callout--bold">{rankName}</span>
          </div>
        ) : null}
      </summary>
      <RankGuts
        isAccordion
        percentFill={percentFill}
        textTitle={title}
        textTitleColor={textTitleColor}
        textTopRight={t("common:winsDashLosses", "{{wins}}W - {{losses}}L", {
          wins,
          losses,
        })}
        barColor={barColor}
      />
    </details>
  );
}

ProfileRank.Stats = Stats;
ProfileRank.Accordion = Accordion;
ProfileRank.AccordionItem = AccordionItem;

export default ProfileRank;
